"use client";

import { type ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";

export enum StepStatus {
  COMPLETED = "completed",
  ACTIVE = "active",
  PENDING = "pending",
}

export type StepConfig = {
  title: string;
  description: string;
  status: StepStatus;
  content: ReactNode;
};

type CryptoComSignupStepperProps = {
  steps: StepConfig[];
};

export const CryptoComSignupStepper = ({ steps }: CryptoComSignupStepperProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hasInitialized = useRef(false);

  // Initial positioning: on first data load, jump to the first incomplete step.
  useEffect(() => {
    if (hasInitialized.current) return;
    const hasAnyCompleted = steps.some((s) => s.status === StepStatus.COMPLETED);
    if (!hasAnyCompleted) return;

    hasInitialized.current = true;
    const firstIncomplete = steps.findIndex((s) => s.status !== StepStatus.COMPLETED);
    const target = firstIncomplete >= 0 ? firstIncomplete : steps.length - 1;
    setSelectedIndex(target);
  }, [steps]);

  // Auto-advance: when the current step becomes "completed", move to the next incomplete step.
  useEffect(() => {
    if (steps[selectedIndex]?.status !== StepStatus.COMPLETED) return;
    const nextIncomplete = steps.findIndex((s, i) => i > selectedIndex && s.status !== StepStatus.COMPLETED);
    if (nextIncomplete >= 0) {
      setSelectedIndex(nextIncomplete);
    }
  }, [steps, selectedIndex]);

  // Display status: real data status + selection highlight for the current step.
  const getDisplayStatus = useCallback(
    (index: number): StepStatus => {
      const dataStatus = steps[index]?.status ?? StepStatus.PENDING;
      if (index === selectedIndex && dataStatus !== StepStatus.COMPLETED) return StepStatus.ACTIVE;
      return dataStatus;
    },
    [steps, selectedIndex],
  );

  // Navigation: allow moving to any completed or active step.
  const canNavigateTo = useCallback(
    (index: number): boolean => {
      if (index < 0 || index >= steps.length) return false;
      if (index === selectedIndex) return false;
      const dataStatus = steps[index]?.status;
      return dataStatus === StepStatus.COMPLETED || dataStatus === StepStatus.ACTIVE;
    },
    [steps, selectedIndex],
  );

  const navigateTo = (index: number) => {
    if (!canNavigateTo(index)) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedIndex(index);
      setIsTransitioning(false);
    }, 200);
  };

  const currentStep = steps[selectedIndex];
  const allCompleted = steps.every((s) => s.status === StepStatus.COMPLETED);

  const canGoPrev = canNavigateTo(selectedIndex - 1);
  const canGoNext = canNavigateTo(selectedIndex + 1);

  return (
    <div
      className="flex flex-col items-center gap-4 w-full max-w-[800px] mx-auto animate-fade-in-up"
      style={{ animationDelay: "150ms", animationFillMode: "backwards" }}
    >
      {/* Stepper dots */}
      <div className="flex items-center justify-between w-full">
        {steps.map((step, index) => {
          const display = getDisplayStatus(index);
          const clickable = canNavigateTo(index);
          return (
            <div key={step.title} className="contents">
              <button
                onClick={() => navigateTo(index)}
                className={cn(
                  "relative z-10 flex flex-col items-center gap-1.5",
                  clickable ? "cursor-pointer" : "cursor-not-allowed",
                )}
              >
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full text-base font-bold transition-all duration-300",
                    display === StepStatus.COMPLETED && "bg-cede-primary text-white",
                    display === StepStatus.ACTIVE && "bg-cede-primary text-white",
                    display === StepStatus.PENDING &&
                      "bg-cede-bg-component border-2 border-cede-border-container text-[#FFFFFF80]",
                  )}
                >
                  {display === StepStatus.COMPLETED ? <Check className="h-5 w-5" /> : index + 1}
                </div>
                <span
                  className={cn(
                    "text-[11px] font-medium text-center leading-tight w-[70px]",
                    display === StepStatus.COMPLETED && "text-cede-text-primary",
                    display === StepStatus.ACTIVE && "text-cede-text-primary",
                    display === StepStatus.PENDING && "text-[#FFFFFF80]",
                  )}
                >
                  {step.title}
                </span>
              </button>

              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-[2px] flex-1 rounded-sm transition-colors duration-500 -mt-5",
                    display === StepStatus.COMPLETED ? "bg-cede-primary" : "bg-[#FFFFFF1F]",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step content card — hidden when all steps are completed */}
      {!allCompleted && (
        <>
          <div className="w-full rounded-2xl border border-cede-border-container bg-cede-bg-component transition-all duration-300">
            <div
              className={cn(
                "flex items-center justify-between gap-6 p-5 transition-all duration-200",
                isTransitioning ? "opacity-0 translate-y-1" : "opacity-100 translate-y-0",
              )}
            >
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-cede-text-primary">
                  {currentStep?.title?.replace(/\n/g, " ")}
                </h2>
                <p className="text-xs text-cede-text-secondary mt-0.5">{currentStep?.description}</p>
              </div>
              <div className="shrink-0">{currentStep?.content}</div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between w-full">
            <Button
              variant="transparent"
              size="small"
              leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
              className={cn(!canGoPrev && "invisible")}
              onClick={() => navigateTo(selectedIndex - 1)}
            >
              Previous
            </Button>
            <Button
              variant="transparent"
              size="small"
              rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
              disabled={!canGoNext}
              className={cn(selectedIndex === steps.length - 1 && "invisible")}
              onClick={() => navigateTo(selectedIndex + 1)}
            >
              Next step
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
